// Catalog.ID Client — Account Unlock Sheet outcome copy
// ============================================================================
// One completion can unlock the account, set a new password, reissue the
// Authenticator Key, any combination, or none of them (registration-restructure
// §12, extended by §15.6). The anonymous flow cannot know which of those
// applied — it never sees the account status — so the server reports it and
// this maps the report onto what the success screen says.

export interface ResetOutcome {
    /** The account was `locked` and is now `active`. */
    unlocked: boolean;
    /** A new password hash was written. */
    passwordChanged: boolean;
    /** A NEW Authenticator Key was minted (round 8, §15.6). The old sheet is
     *  void and the login gate is off until a code confirms the new key. */
    totpReissued?: boolean;
}

/** The success-screen headline: what actually happened, in the member's terms. */
export function resetOutcomeHeadline(outcome: ResetOutcome): string {
    const parts: string[] = [];
    if (outcome.unlocked) parts.push('Account unlocked');
    if (outcome.passwordChanged) parts.push(parts.length === 0 ? 'Password set' : 'password set');
    if (outcome.totpReissued) {
        parts.push(parts.length === 0
            ? 'New Authenticator Key issued'
            : 'new Authenticator Key issued');
    }
    // Nothing to change: an unlocked account whose owner kept their password.
    if (parts.length === 0) return 'Sheet confirmed';
    if (parts.length === 1) return parts[0];
    return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

/** The line under the headline. Every completion signs the account out
 *  everywhere, so that part is unconditional; the rest depends on the outcome. */
export function resetOutcomeDetail(outcome: ResetOutcome): string {
    const signedOut = 'All existing sessions were signed out.';
    const password = outcome.passwordChanged
        ? 'Log in with your Catalog.ID and the new password.'
        : 'Log in with your Catalog.ID and your existing password.';
    if (!outcome.totpReissued) return `${signedOut} ${password}`;
    return `${signedOut} ${password} Your login code is OFF until you scan the new key below and `
        + 'confirm it; the old Authenticator Key sheet no longer works — destroy that printout.';
}
